import type {
  KollectiveContentTokenLoadOptions,
  KollectiveJitcLoadOptions,
  KollectiveLoadOptions,
  KollectivePlugin,
  KsdkEvent,
  KsdkEventData,
} from '../../../@types/ksdk-api';
import type {
  ConnectionStatusEvent,
  PlaybackStatusChangeEvent,
  SourcesReadyEvent,
} from '../../../@types/ksdk-api/events';

import { useCallback, useEffect, useRef, useState } from 'react';
import videojs, { VideoJsPlayer } from 'video.js';

import { useLogger } from '../../../contexts/logger-context';
import { loadScript } from '../../../utils/load-script';

const KSDK_SCRIPT_URL = 'https://cdn.kollective.app/sdk/ksdk-latest.min.js';
const KSDK_SCRIPT_NAME = 'mi-kollective-sdk';

export function useKollective(
  enabled: boolean,
  player: VideoJsPlayer,
  setVideoSrc: (videoSrc: videojs.Tech.SourceObject[], force: boolean) => void,
  config?: KollectiveLoadOptions,
  source?: videojs.Tech.SourceObject
) {
  const { logger } = useLogger();
  const ksdkInstance = useRef<KollectivePlugin>(undefined);
  const ksdkOptionsToken = useRef<string | undefined>(undefined); // Can be the contentToken or integratorToken
  const [ksdkLoaded, setKSDKLoaded] = useState(false);
  const [mediaSource, setMediaSource] = useState<videojs.Tech.SourceObject | undefined>(source);

  useEffect(() => {
    logger.info('Kollective SDK :: Setting media source:', source);
    setMediaSource(source);
  }, [source, logger]);

  const reloadSource = useCallback(() => {
    if (!enabled) {
      logger.info('Kollective SDK :: Reloading source skipped, as the Kollective SDK is not enabled.');
      return;
    }

    logger.info('Kollective SDK :: Reloading source.');
    if (ksdkInstance.current) {
      ksdkInstance.current.stop();
      ksdkInstance.current = undefined;

      if (ksdkOptionsToken.current) {
        logger.info('Kollective SDK :: Resetting options token.');
        ksdkOptionsToken.current = undefined;
      }

      if (mediaSource) {
        setMediaSource({ ...mediaSource });
      }
    }
  }, [enabled, logger, mediaSource]);

  // Check if the Kollective SDK is already loaded
  useEffect(() => {
    if (!enabled) {
      return;
    }
    let alive = true;

    const loadKSDK = async () => {
      await loadScript(KSDK_SCRIPT_URL, KSDK_SCRIPT_NAME);
      if (!alive) {
        return;
      }
      setKSDKLoaded(true);
    };

    if (window.ksdk) {
      setKSDKLoaded(true);
    } else {
      loadKSDK();
    }

    return () => {
      alive = false;
    };
  }, [enabled]);

  // Load and initialize the Kollective SDK
  useEffect(() => {
    if (!ksdkLoaded || !config || !mediaSource) {
      return;
    }

    if (ksdkInstance.current) {
      logger.info('Kollective SDK :: Instance already exists.');

      if (isKollectiveJitcOptions(config)) {
        // If the JITC options are provided, we can assume that the player can play the content
        if (config.integratorToken === ksdkOptionsToken.current) {
          setVideoSrc([mediaSource], true);
          return;
        }
      } else if (isKollectiveRestOptions(config)) {
        // If the REST options are provided, we can assume that the player can play the content
        if (config.contentToken === ksdkOptionsToken.current) {
          setVideoSrc([mediaSource], true);
          return;
        }
      } else {
        const { integratorToken, contentToken, jitc } = config;

        player.error(
          `Kollective SDK :: Error - Reason: Unsupported Kollective options provided.
          IntegratorToken: ${integratorToken}, Jitc: ${jitc}, ContentToken: ${contentToken})}`
        );
        return;
      }
    }

    const sourcesReadyHandler = (event: unknown) => {
      const { publishedSources } = (event as SourcesReadyEvent).data;

      logger.info('Kollective SDK :: Sources Ready. PublishedSource:', publishedSources);
      logger.info('Kollective SDK :: Sources Ready. Set Source:', mediaSource);

      if (isKollectiveJitcOptions(config)) {
        ksdkOptionsToken.current = config.integratorToken;
      } else if (isKollectiveRestOptions(config)) {
        ksdkOptionsToken.current = config.contentToken;
      }

      setVideoSrc([mediaSource], true);
    };

    const errorHandler = (event: unknown) => {
      const { reason, messages } = (event as KsdkEvent<KsdkEventData>).data;

      logger.error(
        `Kollective SDK :: Error - Reason: ${reason}, Messages: ${messages?.join(', ') || 'No additional messages provided.'}`
      );

      player.error(
        `Kollective SDK :: Error - Reason: ${reason}, Messages: ${messages?.join(', ') || 'No additional messages provided.'}`
      );
    };

    const infoHandler = (event: unknown) => {
      const { data } = event as KsdkEvent<KsdkEventData>;

      logger.info('Kollective SDK :: SDK-Information:', data);
    };

    const connectionStatusHandler = (event: unknown) => {
      logger.info(`Kollective SDK :: Connection Status changed: ${(event as ConnectionStatusEvent).state}`, event);
    };

    const playbackStatusChangeHandler = (event: unknown) => {
      const { data } = event as KsdkEvent<PlaybackStatusChangeEvent>;

      logger.info('Kollective SDK :: Playback Status changed:', data);
    };

    if (ksdkInstance.current) {
      logger.info('Kollective SDK :: Instance already exists. Stopping it before re-initialization.');
      // Stop will also cleanup the event listeners
      ksdkInstance.current.stop();
      ksdkInstance.current = undefined;
    } else {
      logger.info('Kollective SDK :: No instance found. Initializing a new one.');
    }

    ksdkInstance.current = window.ksdk.forVideoJS(player, {});
    logger.info('Kollective SDK :: Instance created.');

    ksdkInstance.current.addEventListener(ApiEvents.SOURCES_READY, sourcesReadyHandler);
    ksdkInstance.current.addEventListener(ApiEvents.ERROR, errorHandler);
    ksdkInstance.current.addEventListener(ApiEvents.INFO, infoHandler);
    ksdkInstance.current.addEventListener(ApiEvents.CONNECTION_STATUS_EVENT, connectionStatusHandler);
    ksdkInstance.current.addEventListener(ApiEvents.PLAYBACK_STATUS_CHANGE, playbackStatusChangeHandler);

    logger.info('Kollective SDK :: Load with config:', config);
    ksdkInstance.current.load(config).catch((error) => {
      // Errors are already handled by the error handler
      logger.error('Kollective SDK :: Unexpected error during load:', error);
    });
  }, [ksdkLoaded, logger, player, config, mediaSource, setVideoSrc]);

  return {
    reloadSource,
  };
}

export function isKollectiveRestOptions(options: KollectiveLoadOptions): options is KollectiveContentTokenLoadOptions {
  return (options as KollectiveContentTokenLoadOptions).contentToken !== undefined;
}

export function isKollectiveJitcOptions(options: KollectiveLoadOptions): options is KollectiveJitcLoadOptions {
  return (
    (options as KollectiveJitcLoadOptions).integratorToken !== undefined &&
    (options as KollectiveJitcLoadOptions).jitc !== undefined
  );
}

// This is a copy from the KSDK API, as we cannot import it directly here
// ksdk-api/events.d.ts
enum ApiEvents {
  BITRATE_RANGE_EVENT = 'BitrateRange',
  CONNECTION_STATUS_EVENT = 'ConnectionStatusEvent',
  ECDN10_PROGRESS = 'LegacyProgress',
  ERROR = 'KsdkError',
  INFO = 'KsdkInfo',
  IP_TYPE = 'NetworkType',
  PLAYBACK_STATUS_CHANGE = 'PlaybackStatusChange',
  SOURCES_READY = 'SourcesReady',
  START_DELIVERY_FINISHED = 'StartDeliveryFinished',
  TRANSFER_SUMMARY = 'TransferSummary',
  WEBSOCKET_CONNECT = 'WebsocketConnect',
}
